import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@app/hooks";
import { selectWell, setActiveEvent } from "@app/slices/wellContextSlice";
import { useGetWellsQuery } from "@api/mApi";
import { useGetInboxQuery, type InboxParams } from "@api/pApi";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { StatBand, type Stat } from "@components/ui/StatBand";
import type { InboxItem, WellView } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import {
  EVENT_TYPE_FILTERS,
  reportAge,
  supervisorLabel,
  wellName,
  type EventTypeFilter,
} from "./inboxLabels";

const PAGE_SIZE = 25;

/** Date-range presets → an inclusive `from` bound (today minus N days). */
type DateRange = "" | "7" | "14" | "30" | "90";
const DATE_RANGES: DateRange[] = ["", "7", "14", "30"];

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** Cross-well report feed (the "Bandeja", §3). Triage reports across accessible
 * wells: filter by state / event type / well / date range / mine, paginate by
 * cursor, and deep-link a row into its report. Every number and row is served
 * by Oxbo-P (`GET /v1/inbox`); the FE only resolves labels + lays out. */
export function Inbox() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Filters. Default = open reports, newest first, all wells (§3 landing).
  const [state, setState] = useState<"" | "open" | "closed">("open");
  const [eventType, setEventType] = useState<"" | EventTypeFilter>("");
  const [wellUwi, setWellUwi] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>("14");
  const [mine, setMine] = useState(false);
  // The cursor of the page currently being appended (undefined = first page).
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Register drives the well filter's option list + name resolution (cached).
  const { data: wells = [] } = useGetWellsQuery();
  const wellsByUwi = useMemo(
    () => new Map<string, WellView>(wells.map((w) => [w.uwi, w])),
    [wells],
  );

  const params: InboxParams = useMemo(
    () => ({
      ...(state ? { state } : {}),
      ...(eventType ? { event_type: eventType } : {}),
      ...(wellUwi ? { well_uwi: wellUwi } : {}),
      ...(dateRange ? { from: isoDaysAgo(Number(dateRange)) } : {}),
      ...(mine ? { mine: true } : {}),
      ...(cursor ? { cursor } : {}),
      limit: PAGE_SIZE,
    }),
    [state, eventType, wellUwi, dateRange, mine, cursor],
  );

  const { data, isLoading, isFetching, isError, refetch } = useGetInboxQuery(params);
  const items = useMemo(() => data?.items ?? [], [data]);
  const hasMore = Boolean(data?.next_cursor);

  // Changing a filter must restart pagination from the first page.
  const resetPaging = () => setCursor(undefined);
  const onState = (v: "" | "open" | "closed") => {
    setState(v);
    resetPaging();
  };

  // KPI band — computed over the loaded feed (display-only; §3 header chips).
  const stats: Stat[] = useMemo(() => {
    const open = items.filter((i) => i.state === "open").length;
    const closed = items.length - open;
    const wellsActive = new Set(items.map((i) => i.well_uwi)).size;
    return [
      { label: t("inbox.kpi.open"), value: String(open), tone: "warn" },
      { label: t("inbox.kpi.closed"), value: String(closed), tone: "good" },
      { label: t("inbox.kpi.loaded"), value: String(items.length) },
      { label: t("inbox.kpi.wells"), value: String(wellsActive) },
    ];
  }, [items, t]);

  // Deep-link: select the row's well + event, then drop into its report. Open
  // reports land on the editable Daily Report; closed on the read-only record.
  const openRow = (row: InboxItem) => {
    const well = wellsByUwi.get(row.well_uwi);
    if (well) dispatch(selectWell(well.id));
    dispatch(setActiveEvent(row.event_id));
    navigate(row.state === "open" ? "/daily" : "/well");
  };

  const eventTypeLabel = (type: string): string => {
    const key = `eventType.${type}` as MessageKey;
    return EVENT_TYPE_FILTERS.includes(type as EventTypeFilter) ? t(key) : type;
  };

  const clearFilters = () => {
    setState("open");
    setEventType("");
    setWellUwi("");
    setDateRange("14");
    setMine(false);
    resetPaging();
  };

  return (
    <div>
      <div className="page-head">
        <h1>{t("inbox.title")}</h1>
        <p className="subtitle">{t("inbox.subtitle")}</p>
      </div>

      {/* Data-lineage banner: hidden unless the admin overlay (.lineage-on) is set. */}
      <div className="lineage-banner" role="note">
        <span aria-hidden="true">●</span>
        {t("topbar.lineage")} — {t("topbar.lineage.adminOnly")}
      </div>

      <StatBand stats={stats} />

      <div className="filterbar" style={{ marginTop: 16 }}>
        <div className="field">
          <label htmlFor="ix-state">{t("inbox.filter.state")}</label>
          <select
            id="ix-state"
            value={state}
            onChange={(e) => onState(e.target.value as "" | "open" | "closed")}
          >
            <option value="">{t("inbox.filter.all")}</option>
            <option value="open">{t("inbox.state.open")}</option>
            <option value="closed">{t("inbox.state.closed")}</option>
          </select>
        </div>

        <div className="field">
          <label htmlFor="ix-event">{t("inbox.filter.eventType")}</label>
          <select
            id="ix-event"
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value as "" | EventTypeFilter);
              resetPaging();
            }}
          >
            <option value="">{t("inbox.filter.all")}</option>
            {EVENT_TYPE_FILTERS.map((et) => (
              <option key={et} value={et}>
                {t(`eventType.${et}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="ix-well">{t("inbox.filter.well")}</label>
          <select
            id="ix-well"
            value={wellUwi}
            onChange={(e) => {
              setWellUwi(e.target.value);
              resetPaging();
            }}
          >
            <option value="">{t("inbox.filter.allWells")}</option>
            {wells.map((w) => (
              <option key={w.id} value={w.uwi}>
                {w.commonName}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="ix-range">{t("inbox.filter.dateRange")}</label>
          <select
            id="ix-range"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value as DateRange);
              resetPaging();
            }}
          >
            {DATE_RANGES.map((r) => (
              <option key={r || "all"} value={r}>
                {r ? t("inbox.range.days", { n: Number(r) }) : t("inbox.range.all")}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="ix-mine">{t("inbox.filter.scope")}</label>
          <div className="seg" role="group" aria-label={t("inbox.filter.scope")}>
            <button
              type="button"
              className={mine ? "" : "active"}
              aria-pressed={!mine}
              onClick={() => {
                setMine(false);
                resetPaging();
              }}
            >
              {t("inbox.scope.all")}
            </button>
            <button
              type="button"
              className={mine ? "active" : ""}
              aria-pressed={mine}
              onClick={() => {
                setMine(true);
                resetPaging();
              }}
            >
              {t("inbox.scope.mine")}
            </button>
          </div>
        </div>

        <button
          type="button"
          className="btn"
          onClick={refetch}
          aria-label={t("inbox.refresh")}
          title={t("inbox.refresh")}
          style={{ marginLeft: "auto", alignSelf: "flex-end" }}
        >
          <span aria-hidden="true">⟳</span>
        </button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-scroll">
          <QueryState
            isLoading={isLoading}
            isError={isError}
            isEmpty={items.length === 0}
            emptyMessage={t("inbox.empty")}
            onRetry={refetch}
          >
            <table>
              <thead>
                <tr>
                  <th>{t("inbox.col.state")}</th>
                  <th>{t("inbox.col.well")}</th>
                  <th>{t("inbox.col.eventType")}</th>
                  <th>{t("inbox.col.date")}</th>
                  <th>{t("inbox.col.supervisor")}</th>
                  <th>{t("inbox.col.age")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.daily_report_id}
                    className="selectable"
                    data-owner="p"
                    tabIndex={0}
                    onClick={() => openRow(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openRow(row);
                      }
                    }}
                  >
                    <td>
                      <span
                        className={`badge status-${row.state === "open" ? "drilling" : "suspended"}`}
                      >
                        {row.state === "open" ? t("inbox.state.open") : t("inbox.state.closed")}
                      </span>
                    </td>
                    <td>
                      <span className="well-link">{wellName(row.well_uwi, wellsByUwi)}</span>
                      <div className="mono" style={{ fontSize: 11, color: "var(--dim)" }}>
                        {row.well_uwi}
                      </div>
                    </td>
                    <td>{eventTypeLabel(row.event_type)}</td>
                    <td className="tab-nums">{row.date_report}</td>
                    <td>{supervisorLabel(row.supervisor_1_entry_key)}</td>
                    <td className="tab-nums">{reportAge(row.date_report, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </QueryState>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "center" }}>
        {hasMore ? (
          <button
            type="button"
            className="btn"
            disabled={isFetching}
            onClick={() => setCursor(data?.next_cursor ?? undefined)}
          >
            {isFetching ? t("common.loading") : t("inbox.loadMore")}
          </button>
        ) : null}
        <button type="button" className="btn" onClick={clearFilters}>
          {t("inbox.clearFilters")}
        </button>
      </div>
    </div>
  );
}
